import { Link, usePathname, useRouter } from "@shared/client/navigation";
import { Fragment, useState } from "react";
import {
  ArrowLeft,
  Bot,
  Building2,
  ChartLine,
  Crosshair,
  Database,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  Receipt,
  ScrollText,
  Settings2,
  UserRound,
  UsersRound,
} from "lucide-react";
import { signOut as endSession } from "@shared/lib/auth-client";
import { APP_URL } from "@shared/lib/hosts";
import { Button } from "@shared/components/ui/button";
import { Separator } from "@shared/components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@shared/components/ui/sheet";
import { TooltipProvider } from "@shared/components/ui/tooltip";
import { cn } from "@shared/lib/utils";

const navigation = [
  [
    { href: "/admin", label: "Tableau de bord", icon: LayoutDashboard, exact: true },
    { href: "/admin/prospection", label: "Prospection", icon: Crosshair },
    { href: "/admin/marketing", label: "Marketing", icon: Megaphone },
    { href: "/admin/clients", label: "Clients", icon: Building2 },
    { href: "/admin/users", label: "Utilisateurs", icon: UsersRound },
    { href: "/admin/billing", label: "Facturation", icon: Receipt },
  ],
  [
    { href: "/admin/analytics", label: "Tracking", icon: ChartLine },
    { href: "/admin/agents", label: "Agents IA", icon: Bot },
    { href: "/admin/sources", label: "Sources de données", icon: Database },
    { href: "/admin/logs", label: "Logs", icon: ScrollText },
  ],
  [
    { href: "/admin/settings", label: "Agent & modèle", icon: Settings2 },
  ],
];

const navItem =
  "flex min-h-[36px] items-center gap-3 rounded-md px-2.5 text-[13px] font-medium whitespace-nowrap text-muted-foreground hover:bg-muted hover:text-foreground [&_svg]:size-[17px] [&_svg]:shrink-0";

const navLabel =
  "-translate-x-1 opacity-0 transition-[opacity,transform] duration-150 group-hover:translate-x-0 group-hover:opacity-100 group-has-[:focus-visible]:translate-x-0 group-has-[:focus-visible]:opacity-100 max-md:translate-x-0 max-md:opacity-100";

export function AdminShell({ children, email }: { children: React.ReactNode; email: string }) {
  const pathname = usePathname();
  const router = useRouter();

  async function signOut() {
    await endSession().catch(() => {});
    router.replace("/auth/login");
    router.refresh();
  }

  function isActive(href: string, exact?: boolean) {
    return exact ? pathname === href : pathname === href || pathname.startsWith(`${href}/`);
  }

  const current = navigation.flat().find(({ href, exact }) => isActive(href, exact));
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="grid min-h-screen grid-cols-[58px_1fr] bg-background max-md:block">
        <aside
          className={cn(
            "group sticky top-0 z-40 flex h-screen w-[58px] flex-col overflow-hidden border-r bg-background px-2.5 py-3.5",
            "transition-[width,box-shadow] duration-200 hover:w-[222px] hover:shadow-[0_18px_50px_rgba(20,20,30,0.13)]",
            "has-[:focus-visible]:w-[222px] has-[:focus-visible]:shadow-[0_18px_50px_rgba(20,20,30,0.13)]",
            "max-md:hidden",
          )}
        >
          <Link className="mt-1 ml-1 flex shrink-0 items-center gap-3 text-foreground" href="/">
            <img src="/brand/Logo.svg" alt="Hune" width={64} height={64} className="size-[30px] shrink-0 object-contain" />
            <span className={cn(navLabel, "text-[13.5px] font-semibold tracking-tight")}>Hune</span>
          </Link>

          <nav className="mt-6 grid gap-[3px]">
            {navigation.map((group, index) => (
              <Fragment key={index}>
                {index > 0 && <Separator className="my-2 w-auto mx-1.5" />}
                {group.map(({ href, label, icon: Icon, exact }) => (
                  <Link
                    key={href}
                    className={cn(navItem, isActive(href, exact) && "bg-accent text-foreground")}
                    href={href}
                    title={label}
                  >
                    <Icon />
                    <span className={navLabel}>{label}</span>
                  </Link>
                ))}
              </Fragment>
            ))}
          </nav>
        </aside>

        <div className="flex min-w-0 flex-col">
          <header className="sticky top-0 z-30 flex min-h-[54px] items-center justify-between gap-3.5 border-b bg-background px-6 max-md:gap-2.5 max-md:px-4">
            <div className="flex min-w-0 items-center gap-2.5">
              {/* Mobile : le rail laisse place à un menu, ouvert depuis l'en-tête. */}
              <Sheet onOpenChange={setMenuOpen} open={menuOpen}>
                <SheetTrigger asChild>
                  <Button aria-label="Menu" className="-ml-1.5 md:hidden" size="icon" variant="ghost">
                    <Menu className="size-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[272px] gap-0 p-0" side="left">
                  <SheetTitle className="flex items-center gap-3 border-b px-4 py-3.5 text-[13.5px] font-semibold">
                    <img src="/brand/Logo.svg" alt="" width={64} height={64} className="size-[26px] object-contain" />
                    Hune — administration
                  </SheetTitle>
                  <SheetDescription className="sr-only">Navigation de l’administration</SheetDescription>
                  <nav className="grid gap-[3px] overflow-y-auto p-2.5">
                    {navigation.map((group, index) => (
                      <Fragment key={index}>
                        {index > 0 && <Separator className="my-2 w-auto mx-1.5" />}
                        {group.map(({ href, label, icon: Icon, exact }) => (
                          <Link
                            key={href}
                            className={cn(navItem, "min-h-[42px] text-[14px]", isActive(href, exact) && "bg-accent text-foreground")}
                            href={href}
                            onClick={() => setMenuOpen(false)}
                          >
                            <Icon />
                            {label}
                          </Link>
                        ))}
                      </Fragment>
                    ))}
                    <Separator className="my-2 w-auto mx-1.5" />
                    <a className={cn(navItem, "min-h-[42px] text-[14px]")} href={`${APP_URL}/app`}>
                      <ArrowLeft /> Retour à l’espace client
                    </a>
                  </nav>
                </SheetContent>
              </Sheet>
              <Link className="hidden shrink-0 max-md:inline-flex" href="/admin" aria-label="Tableau de bord">
                <img src="/brand/Logo.svg" alt="" width={64} height={64} className="size-[26px] object-contain" />
              </Link>
              <span className="truncate text-[13.5px] font-semibold tracking-tight">{current?.label ?? "Administration"}</span>
            </div>
            <div className="flex shrink-0 items-center gap-2.5">
              <Button asChild className="text-[12.5px] text-muted-foreground max-md:hidden" size="sm" variant="outline">
                <a href={`${APP_URL}/app`}><ArrowLeft className="size-3.5" /> Retour à l’espace client</a>
              </Button>
              <Button
                aria-label="Déconnexion"
                className="text-muted-foreground"
                onClick={signOut}
                size="sm"
                title={`${email} — Déconnexion`}
                variant="outline"
              >
                <UserRound className="size-4" />
                <LogOut className="size-3.5 opacity-65" />
              </Button>
            </div>
          </header>
          <main className="mx-auto w-[min(1080px,calc(100%-372px))] py-[52px] pb-25 max-[1500px]:w-[min(1080px,calc(100%-220px))] max-[1180px]:w-[calc(100%-90px)] max-md:w-full max-md:px-4 max-md:py-4.5 max-md:pb-12">
            {children}
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
